/**
 * Ability TARGETING HUM — the BG3 pre-cast state.
 *
 * Entering targeting starts a held cue, keyed to the ability's DAMAGE TYPE, falling back to the
 * skill's ESSENCE (body/mind/soul) when it has none. It stops on cancel OR resolve.
 *
 * Design + the full contract: `ABILITY-HUM-WIRING.md` in the SoundForge repo.
 *
 * Owner 2026-10-26: play once at full gain, then hold a faint seamless drone.
 * The optional intro also plays once; two preloaded body elements crossfade without native looping.
 *   `physical` is the ONE exception: no intro, and it does NOT loop.
 *
 * ⛔ THREE THINGS THAT WILL BREAK THIS IF CHANGED CARELESSLY
 *
 * 1. `ftRegisterSiteAudioTransient` (template.js) forgets an element on `pause` as well as
 *    `ended`/`error`. A loop that is stopped and later restarted is therefore NO LONGER
 *    governed by the site master mute unless it re-registers. We register INSIDE the
 *    playback helper immediately before EVERY `play()`, including each drone handoff. Moving it
 *    to the constructor silently breaks mute on the second cast.
 *
 * 2. There is NO right-click cancel and NO click-away cancel. Cancel has many triggers
 *    (Escape in two places, the ✕ button, arming a different ability, losing the FT3D actor)
 *    and every one funnels through cancelAbilityTargeting/confirmAbilityTargeting. Bind the
 *    stop to the LIFECYCLE, never to a gesture, or the loop leaks with no UI to kill it.
 *
 * 3. No AudioContext / Web Audio in production, by decision (music-player.ts, creator-clicks.ts).
 *    Every ramp here is plain HTMLAudioElement `volume` on a timer.
 */

type HumMap = {
    loop?: Record<string, string | null>;
    intro?: Record<string, string | null>;
    damageType?: Record<string, string>;
    essence?: Record<string, string>;
    noLoop?: string[];
};

const HUM_DRONE_GAIN = 0.2;
const HUM_CROSSFADE_SECONDS = 0.25;

function humMap(): HumMap {
    return (window as unknown as { ftAbilityHumSfx?: HumMap }).ftAbilityHumSfx || {};
}

/** Effect-channel volume, read the way lockpick.ts reads it. 0 = muted, so we play nothing. */
function masterVolume(): number {
    const ref = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
    if (!ref) return 0.8;
    if (ref.muted || ref.volume <= 0) return 0;
    return ref.volume;
}

/**
 * Which hum key this ability wants, or null to stay silent.
 *
 * Order matters:
 *  1. DAMAGE TYPE. ⚠ An ability can carry MORE THAN ONE and the player picks — action-bar
 *     branches on `ftLayerEntries(ability,'damage_type').length > 1` and renders a
 *     `input_<abilityId>_user_choice_1_damage_type` select. We read that select first, because
 *     the scalar `data-damage_type` attribute is written with lv() (first value only) and is
 *     therefore ambiguous in exactly the case that matters — a fire/cold flex spell would hum
 *     fire while the player has chosen cold.
 *  2. ESSENCE of the skill. For a skill-driven ability the abilityId IS the skill id (that is
 *     how the contested-skill path already looks itself up in SKILL_CATALOG), so
 *     SKILL_CATALOG[abilityId].essence_used resolves it with no new plumbing.
 *  3. Neither -> null. NOT a generic hum: a mystery drone on a cue we cannot characterise is
 *     worse than silence.
 */
export function ftHumKeyFor(abilityId: string, ability: unknown): string | null {
    const map = humMap();
    const byDamage = map.damageType || {};
    const byEssence = map.essence || {};

    const pick = (raw: unknown): string | null => {
        const id = String(raw || '').toUpperCase();
        return id && byDamage[id] ? byDamage[id] : null;
    };

    // 1a. the player's explicit choice, when the ability offers one
    const sel = document.getElementById(
        `input_${abilityId}_user_choice_1_damage_type`,
    ) as HTMLSelectElement | null;
    const chosen = pick(sel?.value);
    if (chosen) return chosen;

    // 1b. the ability's own damage_type layer(s)
    const entries = (window as unknown as {
        ftLayerEntries?: (node: unknown, layer: string) => unknown[];
    }).ftLayerEntries;
    if (ability && typeof ability === 'object' && typeof entries === 'function') {
        for (const e of entries(ability, 'damage_type') || []) {
            const v = (e && typeof e === 'object')
                ? ((e as Record<string, unknown>).item_id ?? (e as Record<string, unknown>).value)
                : e;
            const hit = pick(v);
            if (hit) return hit;
        }
    }

    // 1c. last resort: the scalar stamped on the button
    const btn = document.querySelector(`[data-ability_id="${CSS.escape(abilityId)}"][data-damage_type]`);
    const attr = pick(btn?.getAttribute('data-damage_type'));
    if (attr) return attr;

    // 2. essence of the skill
    const cat = (window as unknown as {
        SKILL_CATALOG?: Record<string, { essence_used?: string }>;
    }).SKILL_CATALOG || {};
    const skill = cat[abilityId] || cat[abilityId.toUpperCase()] || cat[abilityId.toLowerCase()];
    const ess = String(skill?.essence_used || '').toUpperCase();
    if (ess && byEssence[ess]) return byEssence[ess];

    return null;
}

class AbilityHum {
    private gain = 1;
    private introEl: HTMLAudioElement | null = null;
    private beds: HTMLAudioElement[] = [];
    private weights = [1, 0];
    private current = 0;
    private firstPass = true;
    private fade: { start: number; duration: number } | null = null;
    private key: string | null = null;
    private frame: number | null = null;
    private wake: number | null = null;

    setGain(gain: number): void {
        this.gain = Math.max(0, Math.min(1, gain));
        this.applyVolumes();
    }

    private applyVolumes(): void {
        const volume = masterVolume() * this.gain;
        if (this.introEl) this.introEl.volume = volume;
        this.beds.forEach((el, i) => { el.volume = volume * this.weights[i]!; });
    }

    private play(el: HTMLAudioElement): void {
        // Pause/ended removes the site-mute registration. Re-register every reused element.
        window.ftRegisterSiteAudioTransient?.(el);
        void el.play().catch(() => { /* autoplay/abort */ });
    }

    /** Hold the current cue until stop(); repeated starts never replay the full-gain pass. */
    start(key: string | null, allowSilent = false): void {
        if (!key) { this.stop(); return; }
        if (this.key === key) return;
        this.stop();
        if (masterVolume() * this.gain <= 0 && !allowSilent) return;
        this.key = key;
        const map = humMap();
        try {
            const introUrl = map.intro?.[key];
            if (introUrl) {
                this.introEl = new Audio(introUrl);
                this.applyVolumes();
                this.play(this.introEl);
            }
            const url = map.loop?.[key];
            if (!url) return;
            const sustain = !(map.noLoop ?? []).includes(key);
            this.beds = Array.from({ length: sustain ? 2 : 1 }, () => {
                const el = new Audio(url);
                el.preload = 'auto';
                el.loop = false;
                return el;
            });
            this.applyVolumes();
            this.play(this.beds[0]!);
            if (sustain) this.schedule();
        } catch { this.stop(); }
    }

    private clearClock(): void {
        if (this.frame !== null) window.cancelAnimationFrame(this.frame);
        if (this.wake !== null) window.clearTimeout(this.wake);
        this.frame = this.wake = null;
    }

    private schedule(): void {
        // rAF gives smooth visible fades; the timer also advances audio when a tab is hidden.
        const step = () => { this.clearClock(); this.advance(); };
        this.frame = window.requestAnimationFrame(step);
        this.wake = window.setTimeout(step, 50);
    }

    private advance(): void {
        const outgoing = this.beds[this.current];
        const incoming = this.beds[1 - this.current];
        if (!this.key || !outgoing || !incoming) return;
        const remaining = outgoing.duration - outgoing.currentTime;
        if (!this.fade && Number.isFinite(remaining) && outgoing.duration > 0
            && remaining <= HUM_CROSSFADE_SECONDS) {
            this.fade = { start: outgoing.currentTime, duration: Math.max(0, remaining) };
            incoming.currentTime = 0;
            this.weights[1 - this.current] = 0;
            this.applyVolumes();
            this.play(incoming);
        }
        if (this.fade) {
            // Media time keeps the fade aligned with the actual end, even across dropped frames.
            const p = this.fade.duration > 0
                ? Math.min(1, (outgoing.currentTime - this.fade.start) / this.fade.duration) : 1;
            this.weights[this.current] = (this.firstPass ? 1 : HUM_DRONE_GAIN) * (1 - p);
            this.weights[1 - this.current] = HUM_DRONE_GAIN * p;
            if (p >= 1) {
                outgoing.pause();
                this.current = 1 - this.current;
                this.firstPass = false;
                this.fade = null;
            }
        }
        this.applyVolumes();
        this.schedule();
    }

    /** Cancel, resolve and switch synchronously stop every element and pending handoff. */
    stop(): void {
        this.key = null;
        this.clearClock();
        this.introEl?.pause();
        this.introEl = null;
        this.beds.forEach(el => el.pause());
        this.beds = [];
        this.weights = [1, 0];
        this.current = 0;
        this.firstPass = true;
        this.fade = null;
    }
}

const hum = new AbilityHum();
let currentHumKey: string | null = null;

/** Called when targeting begins. Repeated starts hold the cue; a changed key swaps it. */
export function ftAbilityHumStart(abilityId: string, ability: unknown): void {
    try {
        currentHumKey = ftHumKeyFor(String(abilityId || ''), ability);
        hum.start(currentHumKey);
    } catch { /* never break targeting */ }
}

/** Called from cancelAbilityTargeting AND confirmAbilityTargeting. Idempotent by design. */
export function ftAbilityHumStop(): void {
    currentHumKey = null;
    try { hum.stop(); } catch { /* ignore */ }
}

// Exposed the way creator-clicks.ts exposes window.ftUiClick: any other surface that enters an
// aiming state can drive the same cue, and the staging smoke can assert on real playback
// (which element started, and that it actually STOPPED) rather than on a listener existing.
// The click lane shipped broken twice behind a green smoke that only checked binding.
(window as unknown as Record<string, unknown>).ftAbilityHumStart = ftAbilityHumStart;
(window as unknown as Record<string, unknown>).ftAbilityHumStop = ftAbilityHumStop;
(window as unknown as Record<string, unknown>).ftHumKeyFor = ftHumKeyFor;

// Only the scene's perceived precast entries feed this mixer. Coordinates never cross into audio;
// distance is measured from the active character, independently of the orbit camera/cut slider.
type RemoteHum = { id: string; key: string; distanceFt: number; expiresAtMs: number };
const remoteHums = new Map<string, { key: string; hum: AbilityHum; expiresAtMs: number }>();
function syncRemoteHums(rows: RemoteHum[]): void {
    const keep = new Set<string>();
    const map = humMap();
    for (const row of rows) {
        if (!row || !Number.isFinite(row.distanceFt) || row.distanceFt < 0
            || !Number.isFinite(row.expiresAtMs) || row.expiresAtMs <= Date.now() || !Object.prototype.hasOwnProperty.call(map.loop ?? {}, row.key)
            || !(map.loop?.[row.key] || map.intro?.[row.key])) continue;
        keep.add(row.id);
        let entry = remoteHums.get(row.id);
        if (entry?.key !== row.key) {
            entry?.hum.stop();
            entry = { key: row.key, hum: new AbilityHum(), expiresAtMs: row.expiresAtMs };
            remoteHums.set(row.id, entry);
            entry.hum.setGain(1 / (1 + (row.distanceFt / 20) ** 2));
            entry.hum.start(row.key, true);
        }
        entry.expiresAtMs = row.expiresAtMs;
        entry.hum.setGain(1 / (1 + (row.distanceFt / 20) ** 2));
    }
    for (const [id, entry] of remoteHums) {
        if (!keep.has(id)) { entry.hum.stop(); remoteHums.delete(id); }
    }
}
window.setInterval(() => {
    for (const [id, entry] of remoteHums) {
        if (Date.now() >= entry.expiresAtMs) { entry.hum.stop(); remoteHums.delete(id); }
    }
}, 1000);
window.addEventListener('pagehide', () => syncRemoteHums([]));
Object.assign(window, {
    ftAbilityHumCurrentKey: () => currentHumKey,
    ftSyncRemoteAbilityHums: syncRemoteHums,
});
